import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Account } from 'types/account';
import { useAccount } from 'contexts/account';
import { useUser } from 'contexts/user';
import { isDirty } from 'components/form-fields/formik/utils/forms';
import FormHeader from 'screens/Settings/components/atoms/FormHeader';
import {
  Divider,
  Form,
  Button,
  Message,
  Loader,
  Select,
  Input,
} from 'semantic';
import { useFeatures } from 'contexts/features';
import { currentUserCanAccess, currentUserIsSuperAdmin } from 'utils/roles';
import { useTranslation } from 'react-i18next';
import { FeatureFlag } from 'components';
import CardPlans from 'screens/Settings/screens/organization/BillingAndPlans/CardPlans';
import InputField from 'components/form-fields/formik/InputField';
import FormVerticalSpace from 'screens/Settings/components/atoms/FormVerticalSpace';
import SelectCountry from 'components/form-fields/formik/SelectCountry';
import { Group } from 'common/layout/helpers';
import {
  getManualInvoicingFeeWithVAT,
  usePaymentMethodOptions,
} from 'hooks/usePaymentMethodOptions';
import {
  PaymentMethodsWidget,
  tryStoringPaymentInstrument,
} from 'components/PaymentMethodsWidget/PaymentMethodsWidget';
import CurrentPlan from 'screens/Settings/components/organisms/CurrentPlan';
import request from 'utils/api/request';
import { useEnabledPaymentMethods } from 'contexts/enabledPaymentMethods';
import { useAccountBillingValidation } from './billing-and-plans.validation';
import { useToast } from 'components/Toast';
import { useAccountPaymentInstruments } from 'contexts/UserPaymentInstruments';

type AccountBillingFormValues = {
  billing: {
    accountHolderName?: string;
    ibanNo?: string;
    address: string;
    addressLine2: string;
    postalCode: string;
    city: string;
    countryCode: string;
    paymentMethod: 'autopay' | 'manual';
    sepaAccepted?: boolean;
    sepaAcceptedAt?: Date | null;
    cpoReferenceText: string;
    mspReferenceText: string;
  };
};

export default function AccountBillingFormV2() {
  const { t } = useTranslation();

  const [isBillingDetailsDirty, setIsBillingDetailsDirty] = useState(false);
  const [isBillingDetailsValid, setIsBillingDetailsValid] = useState(false);
  const { enabledPaymentMethods, isPayBridgeEnabled } =
    useEnabledPaymentMethods();
  const [errorMessage, setErrorMessage] = useState<null | string>('');
  const { account, getAccount } = useAccount();
  const { provider } = useUser();

  const toast = useToast();

  useEffect(() => {
    if (!account || !enabledPaymentMethods) {
      return;
    }

    tryStoringPaymentInstrument(account, enabledPaymentMethods)
      .then((isSaving) => {
        if (isSaving) {
          toast.success(
            t('billing.paymentMethodStored', 'Payment method stored!')
          );
          getAccount?.();
        }
      })
      .catch((error) => {
        toast.error(
          t(
            'billing.paymentMethodStoreFailed',
            'Storing payment method failed!'
          )
        );
        setErrorMessage(error.message);
      });
  }, [account, enabledPaymentMethods]);

  const cannotEditBillingInformation = !currentUserCanAccess(
    'accounts-billing',
    'write'
  );

  if (!provider) {
    return <Message error>Provider unknown!</Message>;
  }

  return (
    <>
      {errorMessage && (
        <Message
          error
          content={errorMessage}
          onDismiss={() => setErrorMessage(null)}
        />
      )}

      {account && (
        <>
          <BillingDetails
            key={`bd-${account.id}-${account.billing?.sepaAcceptedAt}`}
            account={account}
            onValidChange={setIsBillingDetailsValid}
            onDirtyChange={setIsBillingDetailsDirty}
            cannotEditBillingInformation={cannotEditBillingInformation}
            refreshAccount={() => {
              getAccount?.();
            }}
          />
          <PaymentInformation
            key={`pi-${account.id}-${account.billing?.paymentMethod}-${account.billing?.sepaAcceptedAt}`}
            account={account}
            isBillingDetailsValid={isBillingDetailsValid}
            isBillingDetailsDirty={isBillingDetailsDirty}
            cannotEditBillingInformation={cannotEditBillingInformation}
            isPayBridgeEnabled={isPayBridgeEnabled}
            refreshAccount={() => {
              getAccount?.();
            }}
          />
        </>
      )}
    </>
  );
}

type BillingDetailsProps = {
  onValidChange: (isValid: boolean) => void;
  onDirtyChange: (isDirty: boolean) => void;
  account: Account;
  refreshAccount?: () => void;
  cannotEditBillingInformation?: boolean;
};
export function BillingDetails({
  account,
  onValidChange,
  onDirtyChange,
  refreshAccount,
  cannotEditBillingInformation,
}: BillingDetailsProps) {
  const [errorMessage, setErrorMessage] = useState<null | string>('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const { t } = useTranslation();
  const { user } = useUser();

  const { isReady: isFeaturesReady } = useFeatures();

  const handleSubmit = async (values: AccountBillingFormValues) => {
    setLoading(true);
    setErrorMessage('');
    setSuccess(false);

    try {
      await request({
        method: 'PATCH',
        path: '1/accounts/self',
        body: values,
      });
      setSuccess(true);
      refreshAccount?.();
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const initialFormValues = {
    billing: {
      bicNo: account?.billing?.bicNo || '',
      address: account?.billing?.address || '',
      addressLine2: account?.billing?.addressLine2 || '',
      postalCode: account?.billing?.postalCode || '',
      city: account?.billing?.city || '',
      countryCode: account?.billing?.countryCode || '',
      paymentMethod: account?.billing?.paymentMethod || 'autopay',
      email: account?.billing?.email || '',
      cpoReferenceText: account?.billing?.cpoReferenceText || '',
      mspReferenceText: account?.billing?.mspReferenceText || '',
      sepaAccepted: account?.billing?.sepaAccepted || false,
    },
  };

  useEffect(() => {
    if (
      account.billing?.address !== '' &&
      account.billing?.countryCode != '' &&
      account.billing?.postalCode !== '' &&
      account.billing?.city !== '' &&
      account.billing?.addressLine2 !== ''
    ) {
      onValidChange(true);
      onDirtyChange(false);
    }
  }, [account]);

  const canEditCountryCode =
    currentUserIsSuperAdmin() || !initialFormValues.billing.countryCode;

  const validationSchema = useAccountBillingValidation();

  return (
    <>
      {(loading && !account) ||
        (!isFeaturesReady && (
          <Loader active={true}>{t('query.states.loading', 'Loading')}</Loader>
        ))}
      <Formik
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        validateOnChange
        onSubmit={handleSubmit}>
        {({ handleSubmit, isSubmitting, values, validateForm }) => {
          const dirty = isDirty(values, initialFormValues);

          useEffect(() => {
            validateForm(values).then((e) => {
              const isFormValid = Object.keys(e ?? {}).length === 0;
              onValidChange(isFormValid);
            });
            onDirtyChange(dirty);
          }, [values, dirty]);

          return (
            <Form onSubmit={handleSubmit}>
              <FormHeader
                size="large"
                pt={5}
                pb={5}
                text={t(
                  'setting.personal.accountBillingAndPlans.title',
                  'Billing & Plans'
                )}
              />
              <Divider />
              {errorMessage && <Message error content={errorMessage} />}
              {success && (
                <Message
                  success
                  content={t('messages.updated', 'Successfully updated!')}
                />
              )}
              <p>
                {t(
                  'settings.organization.billing.description',
                  'Provide your organizations payment & billing details information.'
                )}
              </p>
              {account?.accountTierBillingPlanId && (
                <div style={{ marginTop: 20, marginBottom: 20 }}>
                  <CurrentPlan
                    currentPlanId={
                      account.accountTierBillingPlanId?.id
                        ? account.accountTierBillingPlanId?.id
                        : account.accountTierBillingPlanId
                    }
                    accountBillingCurrency={account.billingCurrency}
                    currentLanguage={user?.defaultLangCode}
                  />
                </div>
              )}
              {cannotEditBillingInformation && (
                <Message info>
                  {t(
                    'access.cannotEditBillingInformation',
                    'You do not have sufficient permissions to edit billing information.'
                  )}
                </Message>
              )}
              <FeatureFlag feature="display_account_card_plans">
                <CardPlans />
              </FeatureFlag>
              <FormHeader
                size="medium"
                disabled={cannotEditBillingInformation}
                mt={20}
                mb={15}
                text={t(
                  'settings.organization.billing.headers.addressInfo',
                  'Billing Address'
                )}
              />
              <InputField
                disabled={cannotEditBillingInformation}
                name="billing.email"
                label={t(
                  'settings.organization.billing.email',
                  'Billing E-mail'
                )}
                required
                wrapperStyle={{ maxWidth: 335 }}
              />
              <FormVerticalSpace />
              <SelectCountry
                disabled={!canEditCountryCode}
                name="billing.countryCode"
                required={true}
                label="Country"
                wrapperStyle={{ maxWidth: 335 }}
              />
              <FormVerticalSpace />
              <Group align="flex-start" spacing={'xs'}>
                <InputField
                  disabled={cannotEditBillingInformation}
                  name="billing.address"
                  label={t('settings.organization.billing.address', 'Street')}
                  required
                  wrapperStyle={{ maxWidth: 285 }}
                />

                <InputField
                  disabled={cannotEditBillingInformation}
                  name="billing.addressLine2"
                  label={t('settings.organization.billing.addressLine2', 'No.')}
                  required
                  wrapperStyle={{ maxWidth: 133 }}
                />
              </Group>
              <FormVerticalSpace />
              <Group align="flex-start" spacing={'xs'}>
                <InputField
                  disabled={cannotEditBillingInformation}
                  name="billing.postalCode"
                  label={t(
                    'settings.organization.billing.postalCode',
                    'Post Code'
                  )}
                  required
                  wrapperStyle={{ maxWidth: 188 }}
                />

                <InputField
                  disabled={cannotEditBillingInformation}
                  name="billing.city"
                  label={t('settings.organization.billing.city', 'City/Town')}
                  required
                  wrapperStyle={{ maxWidth: 133 }}
                />
              </Group>
              <FormVerticalSpace size={40} />
              <InputField
                disabled={cannotEditBillingInformation}
                name="billing.cpoReferenceText"
                label={t(
                  'settings.organization.billing.cpoReferenceText',
                  'Invoice Reference (Charging station)'
                )}
                placeholder={t(
                  'settings.organization.billing.placeholders.cpoReference',
                  'Charging station subscriptions from Road'
                )}
                wrapperStyle={{ maxWidth: 335 }}
              />
              <FormVerticalSpace />
              <InputField
                disabled={cannotEditBillingInformation}
                name="billing.mspReferenceText"
                label={t(
                  'settings.organization.billing.mspReferenceText',
                  'Invoice Reference (Cards)'
                )}
                placeholder={t(
                  'settings.organization.billing.placeholders.mspReference',
                  'Charge Cards usage & subscription from Road'
                )}
                wrapperStyle={{ maxWidth: 335 }}
              />
              <FormVerticalSpace />
              <Button
                type="submit"
                loading={loading || isSubmitting}
                disabled={cannotEditBillingInformation || !dirty}
                style={{ marginLeft: 0 }}>
                {t(
                  'settings.personal.billing.updateBillingInformation',
                  'Update Billing Information'
                )}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

type PaymentInformationProps = {
  account: Account;
  isBillingDetailsDirty: boolean;
  isBillingDetailsValid: boolean;
  cannotEditBillingInformation?: boolean;
  refreshAccount?: () => void;
  isPayBridgeEnabled: boolean;
};
function PaymentInformation({
  account,
  isBillingDetailsValid,
  isBillingDetailsDirty,
  cannotEditBillingInformation,
  isPayBridgeEnabled,
  refreshAccount,
}: PaymentInformationProps) {
  const { t } = useTranslation();
  const [paymentMethod, setPaymentMethod] = useState<string>(
    account?.billing?.paymentMethod || 'autopay'
  );
  const [errorMessage, setErrorMessage] = useState<null | string>('');
  // To show/hide Payment adding widget
  const [isAddingPaymentMethod, setIsAddingPaymentMethod] = useState(false);

  const { accountPaymentInstruments } = useAccountPaymentInstruments();

  useEffect(() => {
    if (isBillingDetailsDirty || !isBillingDetailsValid) {
      setIsAddingPaymentMethod(false);
      return;
    }
  }, [isBillingDetailsDirty, isBillingDetailsValid]);

  const { user, provider } = useUser();

  if (!provider || !user) {
    return <div />;
  }

  const paymentMethodOptions = usePaymentMethodOptions(
    provider,
    account?.billing?.countryCode?.toUpperCase(),
    account?.billingCurrency
  );

  const manualInvoicingFee = getManualInvoicingFeeWithVAT(
    provider,
    account?.billingCurrency
  );

  const isPaymentManual = paymentMethod === 'manual';
  const isPaymentAutopay = paymentMethod === 'autopay';

  const showAccountHolderNameAndIbanFields =
    isPaymentAutopay &&
    account?.billing?.accountHolderName &&
    account?.billing?.ibanNo;

  const handleSubmit = async (values: AccountBillingFormValues) => {
    try {
      await request({
        method: 'PATCH',
        path: '1/accounts/self',
        body: values,
      });
      refreshAccount?.();
    } catch (error: any) {
      setErrorMessage(error.message);
    }
  };

  return (
    <>
      <FormHeader
        size="medium"
        disabled={cannotEditBillingInformation}
        mt={20}
        mb={5}
        text={t(
          'settings.organization.billing.headers.paymentInfo',
          'Payment Information'
        )}
      />
      {!isPayBridgeEnabled && (
        <div>
          <Select
            value={paymentMethod}
            disabled={cannotEditBillingInformation}
            onChange={(e, { value }) => {
              console.log('value', value);
              setPaymentMethod(value as string);
            }}
            label={t(
              'settings.organization.billing.paymentMethod',
              'Payment Method'
            )}
            required
            options={paymentMethodOptions}
            wrapperStyle={{ maxWidth: 285 }}
          />
        </div>
      )}
      {isPaymentManual && (
        <p style={{ fontStyle: 'italic', marginBottom: 0 }}>
          {t(
            'settings.organization.billing.manualPaymentInfo',
            'Cost for processing manual payments is {{fee}} per invoice.',
            {
              fee: manualInvoicingFee,
            }
          )}
        </p>
      )}
      {showAccountHolderNameAndIbanFields && (
        <>
          <FormVerticalSpace />
          <div>
            <Input
              disabled
              label={t(
                'settings.organization.billing.accountHolderName',
                'Account Holder Name'
              )}
              required={isPaymentAutopay}
              value={account?.billing?.accountHolderName}
              wrapperStyle={{ maxWidth: 285 }}
            />
          </div>
          <FormVerticalSpace />
          <div>
            <Input
              disabled
              label={t('settings.organization.billing.ibanNo', 'IBAN Number')}
              required={isPaymentAutopay}
              value={account?.billing?.ibanNo}
              wrapperStyle={{ maxWidth: 285 }}
            />
          </div>
        </>
      )}
      {!showAccountHolderNameAndIbanFields &&
        isPayBridgeEnabled &&
        accountPaymentInstruments?.length === 0 && (
          <Message info>
            {t(
              'billing.noPaymentMethods',
              'You do not have any payment methods added.'
            )}
          </Message>
        )}
      {
        <div style={{ margin: '1rem 0' }}>
          {!isAddingPaymentMethod && (
            <>
              <Button
                type="button"
                disabled={isBillingDetailsDirty || !isBillingDetailsValid}
                onClick={() => {
                  if (!isBillingDetailsDirty && isBillingDetailsValid) {
                    setIsAddingPaymentMethod(true);
                  }
                }}>
                {accountPaymentInstruments?.length === 0
                  ? t('billing.addPaymentMethod', 'Add Payment Method')
                  : t('billing.replacePaymentMethod', 'Replace Payment Method')}
              </Button>
              {(isBillingDetailsDirty || !isBillingDetailsValid) && (
                <Message info>
                  {t(
                    'billing.updateBillingAddressBeforeSubmitting',
                    'Please update billing address before adding a new payment method'
                  )}
                </Message>
              )}
            </>
          )}
          {isAddingPaymentMethod && (
            <>
              {isBillingDetailsValid && !isBillingDetailsDirty && (
                <PaymentMethodsWidget />
              )}
              <Button
                type="button"
                onClick={() => {
                  setIsAddingPaymentMethod(false);
                }}>
                {t('billing.cancePaymentMethodAdding', 'Cancel')}
              </Button>
            </>
          )}
        </div>
      }
      <FormVerticalSpace />
    </>
  );
}
